import React, { useState } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, TextField, MenuItem, Button,
    Chip, LinearProgress, Avatar, InputAdornment
} from '@mui/material';
import {
    Search as SearchIcon, Download as DownloadIcon,
    TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon,
    School as SchoolIcon, EmojiEvents as TrophyIcon,
    People as PeopleIcon, Assessment as AssessmentIcon
} from '@mui/icons-material';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import 'chart.js/auto';

const StudentAnalytics = () => {
    const [classFilter, setClassFilter] = useState('');

    const classPerformance = [
        { class: 'Class 10A', students: 45, avgMarks: 72, passRate: 89, topScore: 96, attendance: 92 },
        { class: 'Class 10B', students: 42, avgMarks: 68, passRate: 83, topScore: 94, attendance: 88 },
        { class: 'Class 9A', students: 40, avgMarks: 75, passRate: 92, topScore: 98, attendance: 91 },
        { class: 'Class 9B', students: 38, avgMarks: 65, passRate: 79, topScore: 90, attendance: 85 },
        { class: 'Class 8A', students: 44, avgMarks: 78, passRate: 95, topScore: 97, attendance: 93 },
        { class: 'Class 7A', students: 40, avgMarks: 70, passRate: 88, topScore: 92, attendance: 90 },
    ];

    const subjectData = {
        labels: ['Maths', 'Science', 'English', 'Hindi', 'Social', 'Marathi'],
        datasets: [{
            label: 'Avg Marks',
            data: [72, 68, 78, 74, 70, 76],
            backgroundColor: ['#e91e63', '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4'],
            borderRadius: 4, barThickness: 30,
        }],
    };

    const trendData = {
        labels: ['Apr', 'Jun', 'Aug', 'Oct', 'Dec', 'Feb'],
        datasets: [
            { label: 'Avg Marks', data: [65, 68, 72, 70, 74, 72], borderColor: '#1a9e8f', backgroundColor: 'rgba(26,158,143,0.1)', fill: true, tension: 0.4 },
            { label: 'Attendance %', data: [88, 90, 85, 92, 87, 91], borderColor: '#2196f3', backgroundColor: 'rgba(33,150,243,0.1)', fill: true, tension: 0.4 },
        ],
    };

    const genderGapData = {
        labels: ['Boys', 'Girls'],
        datasets: [{ data: [70, 75], backgroundColor: ['#2196f3', '#e91e63'], borderWidth: 0 }],
    };

    const topPerformers = [
        { name: 'Priya Desai', class: 'Class 8A', marks: 97, rank: 1 },
        { name: 'Aarav Patil', class: 'Class 10A', marks: 96, rank: 2 },
        { name: 'Sneha Sharma', class: 'Class 9A', marks: 95, rank: 3 },
        { name: 'Vikram Jadhav', class: 'Class 10B', marks: 94, rank: 4 },
        { name: 'Diya Wagh', class: 'Class 9A', marks: 93, rank: 5 },
    ];

    const avgMarks = Math.round(classPerformance.reduce((a, c) => a + c.avgMarks, 0) / classPerformance.length);
    const avgPassRate = Math.round(classPerformance.reduce((a, c) => a + c.passRate, 0) / classPerformance.length);
    const totalStudents = classPerformance.reduce((a, c) => a + c.students, 0);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssessmentIcon sx={{ color: '#1a9e8f', fontSize: 28 }} />
                    <Typography variant="h5" fontWeight={700}>Student Analytics</Typography>
                </Box>
                <Button variant="outlined" startIcon={<DownloadIcon />} size="small">Export Report</Button>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #1a9e8f, #26c6b8)' }}>
                        <CardContent sx={{ textAlign: 'center', color: '#fff' }}>
                            <PeopleIcon sx={{ fontSize: 28, mb: 0.5 }} />
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Students</Typography>
                            <Typography variant="h3" fontWeight={700}>{totalStudents}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #2196f3' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Avg Marks</Typography>
                            <Typography variant="h4" fontWeight={700}>{avgMarks}%</Typography>
                            <Chip label="+3% vs last term" size="small" icon={<TrendingUpIcon />} color="success" />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #4caf50' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Avg Pass Rate</Typography>
                            <Typography variant="h4" fontWeight={700}>{avgPassRate}%</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #ff9800' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Highest Score</Typography>
                            <Typography variant="h4" fontWeight={700}>{Math.max(...classPerformance.map(c => c.topScore))}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Subject-wise performance */}
                <Grid item xs={12} md={8}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Subject-wise Average Marks</Typography>
                            <Box height={250}>
                                <Bar data={subjectData} options={{
                                    responsive: true, maintainAspectRatio: false,
                                    plugins: { legend: { display: false } },
                                    scales: { y: { beginAtZero: true, max: 100, grid: { color: '#f0f0f0' } }, x: { grid: { display: false } } }
                                }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                {/* Gender gap */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Gender Performance Gap</Typography>
                            <Box sx={{ maxWidth: 180, mx: 'auto' }}>
                                <Doughnut data={genderGapData} options={{ plugins: { legend: { position: 'bottom' } }, cutout: '65%' }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>Girls outperform by 5%</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Performance trend */}
                <Grid item xs={12} md={7}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Performance Trend (Academic Year)</Typography>
                            <Box height={200}>
                                <Line data={trendData} options={{
                                    responsive: true, maintainAspectRatio: false,
                                    plugins: { legend: { position: 'top' } },
                                    scales: { y: { min: 50, max: 100, grid: { color: '#f0f0f0' } }, x: { grid: { display: false } } }
                                }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                {/* Top performers */}
                <Grid item xs={12} md={5}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <TrophyIcon sx={{ color: '#ff9800' }} />
                                <Typography variant="subtitle2" fontWeight={700}>Top Performers</Typography>
                            </Box>
                            {topPerformers.map((s, i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.8, borderBottom: i < topPerformers.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                    <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: i === 0 ? '#ff9800' : i === 1 ? '#9e9e9e' : i === 2 ? '#cd7f32' : '#e0e0e0', color: i < 3 ? '#fff' : '#666' }}>{s.rank}</Avatar>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{s.class}</Typography>
                                    </Box>
                                    <Chip label={`${s.marks}%`} size="small" color={s.marks >= 95 ? 'success' : 'primary'} />
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Class-wise Performance */}
            <Card>
                <CardContent>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>Class-wise Performance Summary</Typography>
                    <Grid container spacing={2}>
                        {classPerformance.map(c => (
                            <Grid item xs={12} sm={6} md={4} key={c.class}>
                                <Card variant="outlined">
                                    <CardContent sx={{ p: '12px !important' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2" fontWeight={700}>{c.class}</Typography>
                                            <Chip label={`${c.students} students`} size="small" />
                                        </Box>
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}><Typography variant="caption" color="text.secondary">Avg Marks</Typography><Typography variant="body2" fontWeight={600}>{c.avgMarks}%</Typography></Grid>
                                            <Grid item xs={6}><Typography variant="caption" color="text.secondary">Pass Rate</Typography><Typography variant="body2" fontWeight={600}>{c.passRate}%</Typography></Grid>
                                            <Grid item xs={6}><Typography variant="caption" color="text.secondary">Top Score</Typography><Typography variant="body2" fontWeight={600}>{c.topScore}</Typography></Grid>
                                            <Grid item xs={6}><Typography variant="caption" color="text.secondary">Attendance</Typography><Typography variant="body2" fontWeight={600}>{c.attendance}%</Typography></Grid>
                                        </Grid>
                                        <LinearProgress variant="determinate" value={c.passRate}
                                            sx={{ mt: 1, height: 4, borderRadius: 2, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: c.passRate >= 90 ? '#4caf50' : c.passRate >= 80 ? '#ff9800' : '#e53935' } }} />
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
};

export default StudentAnalytics;
